import { InMemoryWebStorage, WebStorageStateStore, type UserManagerSettings } from "oidc-client-ts";

export interface OidcOptions {
  /** Realm URL, e.g. http://auth.localhost/realms/roastery-manager. */
  authority: string;
  clientId: string;
  /** Where Keycloak sends the browser back; defaults to the current origin. */
  redirectUri?: string;
  /**
   * Where signing *out* lands (US-23.6); defaults to the sign-in address, which is the one place a
   * person who has just signed out did not ask to go — it sends them straight back to a login screen.
   * Whatever is passed here has to be in the client's `post.logout.redirect.uris`, or Keycloak ends
   * the session and then refuses to redirect.
   */
  postLogoutRedirectUri?: string;
  /**
   * The language the application is being read in, passed on to the sign-in screen as `ui_locales`
   * (US-23.1). Without it Keycloak answers from the browser's `Accept-Language`, so somebody working
   * in Polish on an English laptop is signed out into English and back into Polish.
   */
  uiLocales?: string;
}

/**
 * Authorization code + PKCE against Keycloak.
 *
 * Tokens are kept in memory only (ADR-004): nothing lands in localStorage or sessionStorage, so a
 * stolen tab cannot resurrect a session and a reload re-authenticates silently through the Keycloak
 * SSO cookie. Renewal uses the refresh token rather than a hidden iframe, which keeps working when
 * third-party cookies are blocked.
 */
export function createOidcSettings({
  authority,
  clientId,
  redirectUri,
  postLogoutRedirectUri,
  uiLocales,
}: OidcOptions): UserManagerSettings {
  const origin = redirectUri ?? window.location.origin;
  return {
    authority,
    client_id: clientId,
    redirect_uri: origin,
    post_logout_redirect_uri: postLogoutRedirectUri ?? origin,
    response_type: "code",
    scope: "openid profile email",
    ...(uiLocales ? { extraQueryParams: { ui_locales: uiLocales } } : {}),
    automaticSilentRenew: true,
    // Renew a minute before expiry so an in-flight request never races the clock.
    accessTokenExpiringNotificationTimeInSeconds: 60,
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    monitorSession: false,
  };
}

/** Query parameters the authorization server appends to the redirect; none of them concern the app. */
const oidcCallbackParams = ["code", "state", "session_state", "iss"];

/**
 * Strips the OIDC callback parameters from the address bar while keeping the application's own query
 * string, so the user is left on exactly the page they asked for.
 */
export function cleanupSigninUrl(): void {
  const url = new URL(window.location.href);
  for (const parameter of oidcCallbackParams) {
    url.searchParams.delete(parameter);
  }
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
}
